import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useLocation, useRoutes, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import RequireAuth from './RequireAuth';
import RequireAdmin from './RequireAdmin';
import PublicOnly from './PublicOnly';
import { useAuth } from '../providers/AuthProvider';
import { getPaymentPendingRedirect } from '../utils/payment-pending';

const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const AnalyticsPage = lazy(() => import('../pages/AnalyticsPage'));
const CheckoutCancelledPage = lazy(() => import('../pages/billing/CheckoutCancelledPage'));
const CheckoutResultPage = lazy(() => import('../pages/billing/CheckoutResultPage'));
const CheckoutStartPage = lazy(() => import('../pages/billing/CheckoutStartPage'));
const CoachingWorkspacePage = lazy(() => import('../pages/CoachingWorkspacePage'));
const CompleteNewPasswordPage = lazy(() => import('../pages/auth/CompleteNewPasswordPage'));
const ConsentReviewPage = lazy(() => import('../pages/ConsentReviewPage'));
const CreditPackSuccessPage = lazy(() => import('../pages/billing/CreditPackSuccessPage'));
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const FaqPage = lazy(() => import('../pages/FaqPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const LegalDocumentsPage = lazy(() => import('../pages/LegalDocumentsPage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const MediaPage = lazy(() => import('../pages/MediaPage'));
const PendingSetupResolutionPage = lazy(() => import('../pages/billing/PendingSetupResolutionPage'));
const PlatformActivationPage = lazy(() => import('../pages/onboarding/PlatformActivationPage'));
const PlatformsOnboardingPage = lazy(() => import('../pages/onboarding/PlatformsOnboardingPage'));
const PostCalendarPage = lazy(() => import('../pages/posts/PostCalendarPage'));
const PostComposerPage = lazy(() => import('../pages/posts/PostComposerPage'));
const PostsPage = lazy(() => import('../pages/PostsPage'));
const RecoverUsernamePage = lazy(() => import('../pages/auth/RecoverUsernamePage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const SignupPage = lazy(() => import('../pages/auth/SignupPage'));
const UnsubscribePage = lazy(() => import('../pages/UnsubscribePage'));
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));

const AppShellOutlet = () => (
  <AppShell>
    <Outlet />
  </AppShell>
);

const DashboardRoute = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const checkoutState = (searchParams.get('checkout') ?? '').toLowerCase();
  const paymentPendingRedirect = getPaymentPendingRedirect(user);

  if (checkoutState === 'success') {
    const nextParams = new URLSearchParams();
    const sessionId = searchParams.get('session_id');
    const redirectStatus = searchParams.get('redirect_status');

    if (sessionId) {
      nextParams.set('session_id', sessionId);
    }
    if (redirectStatus) {
      nextParams.set('redirect_status', redirectStatus);
    }

    return <Navigate to={`/billing/checkout/success${nextParams.toString() ? `?${nextParams.toString()}` : ''}`} replace />;
  }

  if (checkoutState === 'cancelled') {
    const nextParams = new URLSearchParams();
    const sessionId = searchParams.get('session_id');
    const upgradeTier = searchParams.get('upgradeTier');
    const billingCycle = searchParams.get('billingCycle');
    const source = searchParams.get('source');
    const trial = searchParams.get('trial');
    const resumeUpgrade = searchParams.get('resumeUpgrade');

    if (sessionId) {
      nextParams.set('session_id', sessionId);
    }
    if (upgradeTier) {
      nextParams.set('upgradeTier', upgradeTier);
    }
    if (billingCycle) {
      nextParams.set('billingCycle', billingCycle);
    }
    if (source) {
      nextParams.set('source', source);
    }
    if (trial) {
      nextParams.set('trial', trial);
    }
    if (resumeUpgrade) {
      nextParams.set('resumeUpgrade', resumeUpgrade);
    }

    return <Navigate to={`/billing/checkout/cancelled${nextParams.toString() ? `?${nextParams.toString()}` : ''}`} replace />;
  }

  if (paymentPendingRedirect) {
    return <Navigate to={paymentPendingRedirect} replace />;
  }

  return <DashboardPage />;
};

const ConsentReviewRoute = () => {
  const { user, isHydrated } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo') ?? '/';
  const safeDestination = redirectTo.startsWith('/') ? redirectTo : '/';
  const needsConsent = Boolean(user?.requiresConsent || user?.restrictedAccess);

  if (!isHydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-default text-text-primary">
        <div className="animate-pulse text-sm text-text-muted">Loading session…</div>
      </div>
    );
  }

  if (!needsConsent) {
    return <Navigate to={safeDestination} replace />;
  }

  return <ConsentReviewPage />;
};

const NotFound = () => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
    <h1 className="text-3xl font-semibold text-teal-400">Coming soon</h1>
    <p className="mt-2 max-w-md text-text-muted">
      We&apos;re still wiring up this experience. Hang tight while we connect all the new backend services.
    </p>
  </div>
);

const AppRoutes = () => {
  const element = useRoutes([
    {
      path: '/legal',
      element: <LegalDocumentsPage />,
    },
    {
      path: '/unsubscribe/:token',
      element: <UnsubscribePage />,
    },
    {
      path: 'billing/checkout/success',
      element: <CheckoutResultPage />,
    },
    {
      path: 'billing/checkout/cancelled',
      element: <CheckoutCancelledPage />,
    },
    {
      element: <RequireAuth />,
      children: [
        {
          path: 'consent-review',
          element: <ConsentReviewRoute />,
        },
        {
          path: 'billing/checkout/start',
          element: <CheckoutStartPage />,
        },
        {
          path: 'billing/pending-setup',
          element: <PendingSetupResolutionPage />,
        },
        {
          path: 'platform-activation',
          element: <PlatformActivationPage />,
        },
        {
          element: <AppShellOutlet />,
          children: [
            {
              index: true,
              element: <DashboardRoute />,
            },
            {
              path: 'analytics',
              element: <AnalyticsPage />,
            },
            {
              path: 'posts',
              element: <PostsPage />,
            },
            {
              path: 'posts/calendar',
              element: <PostCalendarPage />,
            },
            {
              path: 'posts/create',
              element: <PostComposerPage />,
            },
            {
              path: 'posts/new',
              element: <PostComposerPage />,
            },
            {
              path: 'posts/:postId/edit',
              element: <PostComposerPage />,
            },
            {
              path: 'media',
              element: <MediaPage />,
            },
            {
              path: 'faq',
              element: <FaqPage />,
            },
            {
              path: 'settings',
              element: <SettingsPage />,
            },
            {
              path: 'platforms',
              element: <Navigate to="/settings#platforms" replace />,
            },
            {
              path: 'platforms-onboarding',
              element: <PlatformsOnboardingPage />,
            },
            {
              path: 'billing/credit-packs/success',
              element: <CreditPackSuccessPage />,
            },
            {
              path: 'coach',
              element: <CoachingWorkspacePage />,                
            },
            {
              element: <RequireAdmin />,
              children: [
                {
                  path: 'admin',
                  element: <AdminDashboardPage />,                
                },
              ],
            },
            {
              path: '*',
              element: <NotFound />,                
            },
          ],
        },
      ],
    },
    {
      element: <PublicOnly />,
      children: [
        { path: '/login', element: <LoginPage /> },
        { path: '/signup', element: <SignupPage /> },
        { path: '/verify-email', element: <VerifyEmailPage /> },
        { path: '/complete-password', element: <CompleteNewPasswordPage /> },
        { path: '/forgot-password', element: <ForgotPasswordPage /> },
        { path: '/recover-username', element: <RecoverUsernamePage /> },
        { path: '/reset-password', element: <ResetPasswordPage /> },
      ],
    },
  ]);

  return <Suspense fallback={<div className="p-8 text-text-muted-strong">Loading...</div>}>{element}</Suspense>;
};

export default AppRoutes;
